/**
 * File Detail Page
 * Edit page for individual files with metadata, revisions, and comments
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Save, Loader2, Download, Trash2 } from 'lucide-react';
import { useRightSidebar, type SidebarSection } from '@/components/sidebar/index';
import { InfoSidebarDetail } from '@/components/sidebar/details/InfoSidebarDetail';
import { RevisionsSidebarDetail } from '@/components/sidebar/details/RevisionsSidebarDetail';
import { CommentsSidebarDetail } from '@/components/sidebar/details/CommentsSidebarDetail';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface FileItem {
    id: string;
    title: string;
    filename_download: string;
    description: string | null;
    tags: string[] | null;
    type: string;
    filesize: number;
    width: number | null;
    height: number | null;
    uploaded_by: string;
    uploaded_on: string;
    modified_by: string | null;
    modified_on: string | null;
    storage: string;
    folder: string | null;
}

export function FileDetailPage() {
    const { fileId } = useParams<{ fileId: string }>();
    const navigate = useNavigate();
    const [file, setFile] = useState<FileItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState('');
    const [filenameDownload, setFilenameDownload] = useState('');

    // Fetch file data
    useEffect(() => {
        if (!fileId) return;

        const fetchFile = async () => {
            setLoading(true);
            try {
                const response = await api.get<{ data: FileItem }>(`/files/${fileId}`);
                const fileData = response.data;
                setFile(fileData);
                setTitle(fileData.title || '');
                setDescription(fileData.description || '');
                setTags(fileData.tags?.join(', ') || '');
                setFilenameDownload(fileData.filename_download || '');
            } catch (error) {
                console.error('Error fetching file:', error);
                toast.error('Failed to load file');
            } finally {
                setLoading(false);
            }
        };

        fetchFile();
    }, [fileId]);

    // Handle save
    const handleSave = async () => {
        if (!fileId) return;

        setSaving(true);
        try {
            const updates = {
                title: title.trim() || null,
                description: description.trim() || null,
                tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : null,
                filename_download: filenameDownload.trim() || null,
            };

            const response = await api.patch<{ data: FileItem }>(`/files/${fileId}`, updates);
            setFile(response.data);
            toast.success('File updated');
        } catch (error) {
            console.error('Error updating file:', error);
            toast.error('Failed to update file');
        } finally {
            setSaving(false);
        }
    };

    // Handle download
    const handleDownload = () => {
        if (!fileId) return;
        window.open(`${import.meta.env.VITE_API_URL}/assets/${fileId}?download`, '_blank');
    };

    // Handle delete
    const handleDelete = async () => {
        if (!fileId || !confirm('Are you sure you want to delete this file?')) return;

        try {
            await api.delete(`/files/${fileId}`);
            toast.success('File deleted');
            navigate('/admin/files');
        } catch (error) {
            console.error('Error deleting file:', error);
            toast.error('Failed to delete file');
        }
    };

    // Format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Sidebar sections
    const sidebarSections = useMemo<SidebarSection[]>(() => {
        if (!file) return [];

        return [
            {
                id: 'info',
                icon: <></>,
                title: 'Information',
                defaultOpen: true,
                content: (
                    <InfoSidebarDetail
                        collectionName="Files"
                        description={file.description || undefined}
                    />
                ),
            },
            {
                id: 'revisions',
                icon: <></>,
                title: 'Revisions',
                defaultOpen: false,
                content: (
                    <RevisionsSidebarDetail
                        collection="cms_files"
                        itemId={fileId!}
                    />
                ),
            },
            {
                id: 'comments',
                icon: <></>,
                title: 'Comments',
                defaultOpen: false,
                content: (
                    <CommentsSidebarDetail
                        collection="cms_files"
                        itemId={fileId!}
                    />
                ),
            },
        ];
    }, [file, fileId]);

    useRightSidebar(sidebarSections);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!file) {
        return (
            <div className="flex h-full items-center justify-center">
                <p className="text-muted-foreground">File not found</p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border bg-card px-6 py-4">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/admin/files')}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold">
                            {file.title || file.filename_download}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {formatFileSize(file.filesize)} • {file.type}
                            {file.width && file.height && ` • ${file.width} × ${file.height}`}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={handleDownload}>
                        <Download className="mr-2 h-4 w-4" />
                        Download
                    </Button>
                    <Button variant="outline" onClick={handleDelete} className="text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="mr-2 h-4 w-4" />
                        )}
                        Save
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                <div className="mx-auto max-w-4xl space-y-6">
                    {/* Preview */}
                    {file.type.startsWith('image/') && (
                        <div className="rounded-lg border border-border bg-muted/30 p-4">
                            <img
                                src={`${import.meta.env.VITE_API_URL}/assets/${file.id}`}
                                alt={file.title || file.filename_download}
                                className="mx-auto max-h-96 rounded-lg object-contain"
                            />
                        </div>
                    )}

                    <Separator />

                    {/* Form fields */}
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="title">Title</Label>
                            <Input
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter file title"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="filename">Filename (Download)</Label>
                            <Input
                                id="filename"
                                value={filenameDownload}
                                onChange={(e) => setFilenameDownload(e.target.value)}
                                placeholder="Enter download filename"
                            />
                            <p className="text-xs text-muted-foreground">
                                The filename used when downloading this file
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Enter file description"
                                rows={4}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="tags">Tags</Label>
                            <Input
                                id="tags"
                                value={tags}
                                onChange={(e) => setTags(e.target.value)}
                                placeholder="Enter tags separated by commas"
                            />
                            <p className="text-xs text-muted-foreground">
                                Separate multiple tags with commas
                            </p>
                        </div>
                    </div>

                    {/* File metadata */}
                    <Separator />
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium">File Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-muted-foreground">Type:</span>
                                <span className="ml-2">{file.type}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Size:</span>
                                <span className="ml-2">{formatFileSize(file.filesize)}</span>
                            </div>
                            {file.width && file.height && (
                                <div>
                                    <span className="text-muted-foreground">Dimensions:</span>
                                    <span className="ml-2">{file.width} × {file.height}</span>
                                </div>
                            )}
                            <div>
                                <span className="text-muted-foreground">Uploaded:</span>
                                <span className="ml-2">
                                    {formatDistanceToNow(new Date(file.uploaded_on), { addSuffix: true })}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
